import { ModalActionItemInterface } from '../../../interfaces/account-center/ModalActionItemInterface';
import { Utils } from '../../../../../common/js/Utils';
import { CoverpathBaseAccountCenterController } from '../../../../js/controllers/CoverpathBaseAccountCenterController';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import { Component, ViewChild, OnInit } from '@angular/core';
import { Controller } from 'apps/coverpath-client/public/js/Controller';
import { AccountCenterSection } from '../utilities/accountCenterSection.component';
import { AccountCenterTable } from '../account-center-table/account-center-table.component';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { AccountCenterTableRow } from '../../../classes/account-center/AccountCenterTableRow';
import { ActionType } from '../../../classes/account-center/ActionType';
import { CoverpathUtils } from '../../../../js/static/CoverpathUtils';
import { AccountCenterTableCell } from '../../../classes/account-center/AccountCenterTableCell';
import { PremiumDisplay } from '../../../interfaces/Type';
import { Product, TimeFrequency } from '../../../../../common/js/static/Product';
import { InitialInvoice } from '../.././../../../common/js/policyAdmin/interface/InitialInvoiceInfoResponse';
import { PolicyData } from '../../../interfaces/PolicyData';
import type { MMDirectPolicy } from '../../../../classes/mmdirect_subclasses/MMDirectPolicy';
import type { CoverpathPolicy } from '../../../../classes/coverpath_subclasses/CoverpathPolicy';
import { PaymentMode } from '../../../../js/interfaces/CoverpathTypes';
import { Subscription } from 'rxjs';
import { POLICY_TYPES } from '../.././../../../common/js/constants/policy';
import { AlertIntent } from '@hit/coverpath-design-system';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';

const tableCreateDef = [
  //Premium
  {
    show: function () {
      return true;
    },
    header: function (policyData: PolicyData) {
      let recurringPremiumText = null;
      if (CoverpathUtils.isPostIssue(policyData.getCACWorkflowState())) {
        recurringPremiumText =
          'The recurring premium may not reflect fractional premium payments due as a result of a frequency change.';
      }
      return new AccountCenterTableCell('heading', 'Premium', null, recurringPremiumText);
    },
    data: function (policyData: PolicyData, policy: MMDirectPolicy | CoverpathPolicy) {
      if (CoverpathUtils.isPostIssue(policyData.getCACWorkflowState())) {
        const premium = Utils.formatCurrency(policyData.getCACPaymentData().getCACTotalPremium().toString());

        const per = policyData.getCACPaymentData().getCACPaymentFrequency() !== 'semi-annual' ? `/` : ' ';
        const timeFrequencyTxt = policyData.getCACPaymentData().getCACPaymentFrequency()
          ? per + TimeFrequency[policyData.getCACPaymentData().getCACPaymentFrequency()]
          : '';
        return PaymentDetailsTableComponent.hasExtraPremiums(policyData, policy)
          ? new AccountCenterTableCell(
              'data',
              premium + timeFrequencyTxt,
              PaymentDetailsTableComponent.getPremiumBreakdownModalAction(policyData)
            )
          : new AccountCenterTableCell('data', premium + timeFrequencyTxt);
      } else {
        return this.constructPremiumCell(policyData, policy);
      }
    },
    cssClass: function (policyData: PolicyData) {
      if (policyData.getCACPaymentData().getCACNextPaymentDisplayDDBPast()) {
        return 'account-center-table__row__after-alert';
      }
    },
  },
  //Payment Method
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACIsInForce();
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Payment Method');
    },
    data: function (policyData: PolicyData, policy: MMDirectPolicy | CoverpathPolicy) {
      const cacPaymentData = policyData.getCACPaymentData();
      const paymentInfo = `${PaymentMode[cacPaymentData.getCACPaymentMode()]} ${
        cacPaymentData.getCACIsAutoPay() ? '(AutoPay)' : ''
      }`;
      const isPostIssue = CoverpathUtils.isPostIssue(policyData.getCACWorkflowState());
      const needsPaymentAuthorization = isPostIssue && cacPaymentData.getCACNeedsAuthorizationWithinOrPast();
      const hasPaymentFailed = cacPaymentData.getCACHasPaymentFailed();

      if (needsPaymentAuthorization || hasPaymentFailed) {
        return new AccountCenterTableCell(
          'data',
          paymentInfo,
          PaymentDetailsTableComponent.getMakePaymentModalAction(policyData, this.primaryPolicy)
        );
      } else {
        return new AccountCenterTableCell('data', paymentInfo);
      }
    },
  },
  //Next Payment Date
  {
    show: function (policyData: PolicyData) {
      return policyData.getCACIsInForce() && !policyData.getCACIsHidePaymentDate();
    },
    header: function () {
      return new AccountCenterTableCell('heading', 'Next Payment Date');
    },
    data: function (policyData: PolicyData) {
      const cacPaymentData = policyData.getCACPaymentData();
      const nextPaymentDate = policyData.getCACIsHidePaymentDate() ? null : cacPaymentData.getCACNextPaymentDate();
      return new AccountCenterTableCell('data', `${Utils.formatDate(nextPaymentDate)}`);
    },
  },
  //Initial Payment
  {
    show: function (policyData) {
      return this.showInitialPaymentCell(policyData);
    },
    header: function () {
      return new AccountCenterTableCell(
        'heading',
        'Initial Payment',
        null,
        'Since your financial professional set your policy’s effective date in the past, your first payment includes more premium to cover the additional time period'
      );
    },
    data: function (policyData: PolicyData) {
      return this.constructInitialPaymentCell(policyData);
    },
  },
];

@Component({
  selector: 'payment-details-table',
  moduleId: module.id,
  template: '<account-center-table></account-center-table>',
})
export class PaymentDetailsTableComponent extends AccountCenterSection implements OnInit {
  @ViewChild(AccountCenterTable, { static: true }) table: AccountCenterTable;
  static title: string = 'Payment Details';
  controller: Controller;
  accountCenterController: CoverpathBaseAccountCenterController;
  initialInvoiceForBackdatedPrimaryPolicy: number = 0;
  initialInvoiceForBackdatedSecondaryPolicy: number = 0;
  primaryPolicyNumber: string;
  primaryPolicy: MMDirectPolicy | CoverpathPolicy;
  primaryPolicyData: PolicyData;
  secondaryPolicyNumber: string;
  secondaryPolicy: MMDirectPolicy | CoverpathPolicy;
  secondaryPolicyData: PolicyData;
  postIssueDataSusbcription: Subscription;

  constructor(amorphicService: AmorphicService, accountCenterService: AccountCenterService) {
    super(amorphicService, accountCenterService);
    this.controller = amorphicService.controller as Controller;
    this.accountCenterController = this.controller.accountCenterController;
  }

  async ngOnInit(): Promise<void> {
    await this.resetData();
    this.subscribeToAccountCenterService();
  }

  ngOnDestroy() {
    if (this.postIssueDataSusbcription) {
      this.postIssueDataSusbcription.unsubscribe();
    }
  }

  async processWorkflowAction(workflowAction: string): Promise<void> {
    await this.resetData();
  }

  async resetData() {
    if (this.accountCenterService) {
      this.primaryPolicy = this.accountCenterService.application.primaryPolicyWithPendingAmendment;
      this.primaryPolicyNumber = this.primaryPolicy.policyNumber;
      this.primaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(this.primaryPolicy.__id__ as string);

      if (this.primaryPolicyData.getCACIsJointApp()) {
        this.secondaryPolicy = this.accountCenterService.application.secondaryPolicyWithPendingAmendment;
        this.secondaryPolicyNumber = this.secondaryPolicy.policyNumber;
        this.secondaryPolicyData = this.accountCenterService.getPolicyDataFromPolicy(
          this.secondaryPolicy.__id__ as string
        );
      }
    }
    this.table.title = 'Payment Details';
    if (this.primaryPolicyData.getCACPaymentData().getCACNextPaymentDisplayDDBPast()) {
      this.table.alert = {
        intent: AlertIntent.WARNING,
        text: 'Your premium payment is overdue.',
      };
    }
    await this.constructTableForPolicies();
  }

  static getNaCell(): AccountCenterTableCell {
    return new AccountCenterTableCell('data', 'N/A');
  }

  private async calculateInitialInvoiceForBackdating(
    primaryPolicyNumber: string,
    primaryPolicyData: PolicyData,
    secondaryPolicyNumber: string,
    secondaryPolicyData: PolicyData
  ) {
    //If policy is not in force and offer has been sent, only then show first payment
    if (
      primaryPolicyData.getCACIsCoverpathPolicy() &&
      !primaryPolicyData.getCACIsInForce() &&
      !!primaryPolicyData.getCACFinalOfferSentDate() &&
      this.amorphicService.controller.isBackDatingAmendmentEnabled
    ) {
      //calculate first payment only if it's backdating case and frequency is monthly
      this.initialInvoiceForBackdatedPrimaryPolicy = await this.getInvoiceAmmount(
        primaryPolicyData,
        primaryPolicyNumber
      );
      //repeat for secondary policy in case of joint app
      if (secondaryPolicyData) {
        this.initialInvoiceForBackdatedSecondaryPolicy = await this.getInvoiceAmmount(
          secondaryPolicyData,
          secondaryPolicyNumber
        );
      }
    }
  }

  private async getInvoiceAmmount(policyData: PolicyData, policyNumber: string) {
    const policyCACViewData = policyData.getCACPaymentData();
    if (
      policyCACViewData &&
      !!policyCACViewData.getCACEffectiveDate() &&
      (policyData.getCACPaymentData().getCACPaymentFrequency() === 'monthly' ||
        policyData.getCACPaymentData().getCACPaymentFrequency() === 'month')
    ) {
      const initialInvoiceResponse: InitialInvoice = await this.accountCenterController.getInitialInvoiceInfo(
        policyNumber
      );
      return initialInvoiceResponse.amount;
    }
  }

  private async constructTable() {
    const rows: Array<AccountCenterTableRow> = [];

    await this.calculateInitialInvoiceForBackdating(
      this.primaryPolicyNumber,
      this.primaryPolicyData,
      this.secondaryPolicyNumber,
      this.secondaryPolicyData
    );

    if (this.primaryPolicyData.getCACIsJointApp()) {
      try {
        let isPrimaryCanceled = false;
        let isSecondaryCanceled = false;
        if (CoverpathUtils.isPostIssue(this.workflowAction)) {
          isPrimaryCanceled = await this.accountCenterController.isCanceledInPA(this.primaryPolicyNumber);
          isSecondaryCanceled = await this.accountCenterController.isCanceledInPA(this.secondaryPolicyNumber);
        }

        const secondaryPolicyTermLength = this.secondaryPolicyData.getCACTerm();
        const secondaryTermLengthDescription =
          secondaryPolicyTermLength === 1
            ? Product.products.CASTerm.artDescription
            : `${secondaryPolicyTermLength} Year Term`;

        rows.push(
          new AccountCenterTableRow(
            [
              new AccountCenterTableCell('heading', ''),
              new AccountCenterTableCell('data-heading', `Whole Life ${this.primaryPolicyData.getCACTerm()}`),
              new AccountCenterTableCell('data-heading', secondaryTermLengthDescription),
            ],
            'joint-header'
          )
        );

        const promises = [];
        tableCreateDef.forEach((def) => {
          promises.push(
            this.createRowFromDef(
              def,
              this.primaryPolicy,
              this.secondaryPolicy,
              this.primaryPolicyData,
              this.secondaryPolicyData,
              isPrimaryCanceled,
              isSecondaryCanceled
            )
          );
        });
        const temp = await Promise.all(promises);
        const tableRows = temp.filter((row) => row !== undefined);
        rows.push(...tableRows);
      } catch (err) {
        this.table.hideTable = false;
        const error = {
          isHumanRelated: false,
          name: err.name,
          message: err.message,
          stack: err.stack,
          data: {
            primaryPolicyNumber: this.primaryPolicyNumber,
            applicationNumber: this.accountCenterService.application.applicationNumber,
          },
        };
        UILoggerSingleton.getInstance().error({
          module: 'payment-details-table.component',
          function: 'constructTable',
          category: UICategory.request,
          error: error,
        });
      }
    } else {
      const promises = [];
      tableCreateDef.forEach((def) => {
        promises.push(this.createRow(def));
      });
      const tableRows = (await Promise.all(promises)).filter((row) => row !== undefined);
      rows.push(...tableRows);
    }
    return rows;
  }

  private async createRow(def): Promise<AccountCenterTableRow> {
    if (def.show.call(this, this.primaryPolicyData, this.primaryPolicy)) {
      return new AccountCenterTableRow(
        [
          def.header.call(this, this.primaryPolicyData),
          await def.data.call(this, this.primaryPolicyData, this.primaryPolicy),
        ],
        def.cssClass && def.cssClass.call(this, this.primaryPolicyData)
      );
    }
  }

  private async createRowFromDef(
    def,
    primaryPolicy: MMDirectPolicy | CoverpathPolicy,
    secondaryPolicy: MMDirectPolicy | CoverpathPolicy,
    primaryPolicyData: PolicyData,
    secondaryPolicyData: PolicyData,
    isPrimaryCanceled: boolean,
    isSecondaryCanceled: boolean
  ): Promise<AccountCenterTableRow> {
    if (
      def.show.call(this, primaryPolicyData, primaryPolicy) ||
      def.show.call(this, secondaryPolicyData, secondaryPolicy)
    ) {
      const header = def.header.call(this, primaryPolicyData);
      const wholeLifeCell = await def.data.call(this, primaryPolicyData, primaryPolicy);
      wholeLifeCell.setCanceled(isPrimaryCanceled);

      const termCell = await def.data.call(this, secondaryPolicyData, secondaryPolicy);
      termCell.setPolicyLabel(`Term ${secondaryPolicyData.getCACTerm()}`);
      termCell.setCanceled(isSecondaryCanceled);

      return new AccountCenterTableRow([header, wholeLifeCell, termCell]);
    }
  }

  private async constructTableForPolicies(): Promise<void> {
    this.table.isJointApp = this.primaryPolicyData.getCACIsJointApp();
    this.table.rows = await this.constructTable();
  }

  public constructPremiumCell(
    primaryPolicyData: PolicyData,
    policy: MMDirectPolicy | CoverpathPolicy
  ): AccountCenterTableCell {
    const premiumDisplay: PremiumDisplay = primaryPolicyData
      .getCACPaymentData()
      .getCACPremiumDisplayObj(this.workflowAction, primaryPolicyData, false);
    const modalActionItem = PaymentDetailsTableComponent.getPremiumBreakdownModalAction(primaryPolicyData);
    const hasExtraPremiums =
      !/pending review/.test(premiumDisplay.premiumText) &&
      PaymentDetailsTableComponent.hasExtraPremiums(primaryPolicyData, policy);

    return (premiumDisplay.showPremiumBreakdown && hasExtraPremiums) ||
      (primaryPolicyData.getCACFinalOfferSentDate() && primaryPolicyData.getCACIsAnnuallyRenewableTerm())
      ? new AccountCenterTableCell('data', premiumDisplay.premiumText, modalActionItem)
      : new AccountCenterTableCell('data', premiumDisplay.premiumText);
  }

  static getPremiumBreakdownModalAction(primaryPolicyData: PolicyData) {
    return {
      displayText: 'See details',
      actionType: ActionType.OpenModal,
      action: 'premiumBreakdown',
      modalData: {
        isPrimaryPolicy: primaryPolicyData.getCACIsPrimary(),
      },
    } as ModalActionItemInterface;
  }

  static getMakePaymentModalAction(primaryPolicyData: PolicyData, primaryPolicy: MMDirectPolicy | CoverpathPolicy) {
    return {
      displayText: 'Make a payment',
      actionType: ActionType.OpenModal,
      action: 'AuthorizePayment',
      modalData: {
        policy: primaryPolicy,
        paPolicyData: primaryPolicyData,
      },
    } as ModalActionItemInterface;
  }

  static hasExtraPremiums(primaryPolicyData: PolicyData, policy: MMDirectPolicy | CoverpathPolicy) {
    const policyViewData = primaryPolicyData.getCACPaymentData();
    return (
      policy.premiumBreakdown.getCurrentFlatExtras().length > 0 ||
      (primaryPolicyData.getCACHasWaiver() && policyViewData.getCACTotalPremium() !== 0) ||
      primaryPolicyData.getCACPaymentData().getCACRiders().length > 0
    );
  }

  public showInitialPaymentCell(policyData: PolicyData) {
    if (
      policyData.getCACPolicyType() === POLICY_TYPES.AMEND_REMOVE_PRIMARY &&
      policyData.getCACApplicationType() === POLICY_TYPES.AMEND_REMOVE_PRIMARY
    ) {
      return this.initialInvoiceForBackdatedSecondaryPolicy;
    }

    return policyData.getCACPolicyType() === POLICY_TYPES.PRIMARY
      ? this.initialInvoiceForBackdatedPrimaryPolicy > 0 || this.initialInvoiceForBackdatedSecondaryPolicy > 0
      : false;
  }

  public constructInitialPaymentCell(primaryPolicyData: PolicyData) {
    if (
      primaryPolicyData.getCACPolicyType() === POLICY_TYPES.SECONDARY &&
      primaryPolicyData.getCACApplicationType() === POLICY_TYPES.AMEND_REMOVE_PRIMARY &&
      this.initialInvoiceForBackdatedPrimaryPolicy > 0
    ) {
      return new AccountCenterTableCell(
        'data',
        `${Utils.formatCurrency(this.initialInvoiceForBackdatedPrimaryPolicy.toString(), 2)}`
      );
    }

    if (primaryPolicyData.getCACIsPrimary() && this.initialInvoiceForBackdatedPrimaryPolicy > 0) {
      return new AccountCenterTableCell(
        'data',
        `${Utils.formatCurrency(this.initialInvoiceForBackdatedPrimaryPolicy.toString(), 2)}`
      );
    } else if (primaryPolicyData.getCACIsSecondary() && this.initialInvoiceForBackdatedSecondaryPolicy > 0) {
      return new AccountCenterTableCell(
        'data',
        `${Utils.formatCurrency(this.initialInvoiceForBackdatedSecondaryPolicy.toString(), 2)}`
      );
    } else {
      return PaymentDetailsTableComponent.getNaCell();
    }
  }
}
